import { createFileRoute } from '@tanstack/react-router'
import thai2englishApi from 'api/thai2english-api'
import { buildEngWordResult } from 'lib/processQuery'
import { canonicalEngEntry } from 'lib/dictionary-routes'
import {
   canonicalLink,
   definedTermSchema,
   dictionaryDescription,
   dictionaryUrl,
   ldJsonScript,
   pageMeta,
} from 'lib/seo'
import { entryCacheControl } from 'lib/entry-cache'
import EnglishWordPage from 'pages/english-word'

export const Route = createFileRoute('/english/$word')({
   // Runs on the server for a fresh document request and on the client for an
   // in-app navigation, so the entry is in the first byte of HTML a crawler
   // sees and costs no extra round trip once the app has hydrated.
   loader: async ({ params }) => {
      const { word } = params
      const searchResults = await thai2englishApi.searchDictionary(word)
      return buildEngWordResult(canonicalEngEntry(word, searchResults), searchResults)
   },
   head: ({ loaderData }) => {
      // Undefined while the loader is still running, and on the error path.
      if (!loaderData) return {}

      const { word, lemma, meanings } = loaderData.engDictionaryWord
      const url = dictionaryUrl('english', word)
      // An English entry's meanings are its Thai translations, so the snippet
      // reads "hello — สวัสดี; หวัดดี. …".
      const description = dictionaryDescription(
         word,
         undefined,
         meanings.map((m) => m.meaning)
      )
      // An inflected form (ran, glasses) shows its base word's meanings, so as
      // far as a search engine is concerned it is that page. The form keeps its
      // own URL — searching "ran" should still land somewhere that says it is
      // the past tense of "run" — but points its canonical at the base rather
      // than competing with it.
      const canonical = lemma ? dictionaryUrl('english', lemma) : url

      return {
         meta: pageMeta({
            title: `${word} - Thai / English dictionary meaning - ภาษาอังกฤษ แปล ความหมาย`,
            description,
            url,
            ogType: 'article',
         }),
         links: [canonicalLink(canonical)],
         scripts: [ldJsonScript(definedTermSchema({ url: canonical, word, description }))],
      }
   },
   headers: () => entryCacheControl,
   component: EnglishWordPage,
})
